import { useState } from 'react'
import { Button } from '@/components/ui/button'
import LinkButton from '@/components/ui/link-button'
import { unlinkAccount } from 'app/frontend/base/api/user'
import { Check, X, Loader2, RefreshCw } from 'lucide-react'
import { displayErrorToast, imageUrl } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useQueryClient } from '@tanstack/react-query'
import Card from 'app/frontend/base/components/Card'

export const infoByType = {
  Discord: { logo: 'discord-logo-blue.svg', authUrl: '/auth/discord' },
  'Battle.net': { logo: 'bnet-logo-blue.svg', authUrl: '/auth/bnet' },
  Google: { logo: 'google-logo.svg', authUrl: '/auth/google_oauth2' },
  Patreon: { logo: 'patreon-logo.svg', authUrl: '/auth/patreon' },
  'Warcraft Logs': { logo: 'wcl-logo.png', authUrl: '/auth/wcl' },
}

interface Props {
  type: 'Discord' | 'Battle.net' | 'Google' | 'Patreon' | 'Warcraft Logs'
}

const LinkedAccount = ({ type }: Props) => {
  const [unlinking, setUnlinking] = useState(false)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()

  if (!user) return null

  const connection =
    type === 'Battle.net'
      ? ({
          displayName: user.battletag,
          displayType: 'Battle.net',
          details: {},
        } as wowaudit.LinkedAccount)
      : user.linkedAccounts?.find((account) => account.displayType === type)

  const unlink = () => {
    if (!connection || type === 'Battle.net') return
    setUnlinking(true)

    unlinkAccount(connection.id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['pageInfo'] })
      })
      .catch((err) => displayErrorToast(err, t))
      .then(() => setUnlinking(false))
  }

  return (
    <Card className='py-3 px-4 my-2 flex items-center justify-between gap-2 max-w-200'>
      <div className='flex items-center gap-4 min-w-0'>
        <img src={imageUrl(infoByType[type].logo)} className='w-12 shrink-0' />

        <div className='min-w-0'>
          <span className='font-bold text-lg'>{type}</span>
          <div className='text-sm truncate'>{connection?.displayName}</div>
        </div>
      </div>

      <div className='flex gap-4 items-center shrink-0'>
        {connection && type === 'Warcraft Logs' ? (
          <LinkButton
            to={`${infoByType[type].authUrl}?origin=${encodeURIComponent(
              '/profile',
            )}`}
            reloadDocument
            size='icon'
            aria-label='Re-authenticate'
            title='Re-authenticate'
          >
            <RefreshCw size={14} />
          </LinkButton>
        ) : null}

        {connection ? (
          <Button className={`w-28 pointer-events-none`}>
            <span className='flex gap-2 items-center'>
              <Check size={14} />
              Linked
            </span>
          </Button>
        ) : (
          <a href={infoByType[type].authUrl}>
            <Button variant='outline' className={`w-28`}>
              Connect
            </Button>
          </a>
        )}

        {unlinking ? (
          <Loader2 size={20} className='animate-spin' />
        ) : (
          <X
            size={20}
            onClick={unlink}
            className={`transition-colors ${
              connection && type !== 'Battle.net'
                ? 'cursor-pointer hover:text-destructive-foreground'
                : 'opacity-10 pointer-events-none'
            }`}
          />
        )}
      </div>
    </Card>
  )
}

export default LinkedAccount
